import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Card } from "@/components/Shell";
import { Mail, Save, Send as SendIcon, Globe, CheckCircle2, AlertCircle, AlertTriangle, Trash2 } from "lucide-react";

export default function Settings() {
  const [smtp, setSmtp] = useState(null);
  const [app, setApp] = useState({ public_url: "" });
  const [testTo, setTestTo] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [log, setLog] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  // Danger-zone state
  const [showReset, setShowReset] = useState(false);
  const [resetPw, setResetPw] = useState("");
  const [resetConfirm, setResetConfirm] = useState("");
  const [keepUsers, setKeepUsers] = useState(true);
  const [resetResult, setResetResult] = useState(null);
  const [resetBusy, setResetBusy] = useState(false);

  const load = async () => {
    const s = await api.get("/settings/smtp"); setSmtp(s.data);
    const a = await api.get("/settings/app"); setApp(a.data);
    const l = await api.get("/settings/email-log"); setLog(l.data);
  };
  useEffect(() => { load().catch(e=>setErr(formatApiError(e))); }, []);

  const saveSmtp = async (e) => {
    e?.preventDefault?.(); setErr(""); setBusy(true);
    try {
      const payload = { ...smtp };
      delete payload.key;
      await api.put("/settings/smtp", payload);
      await load();
      setTestResult({ ok: true, msg: "SMTP settings saved." });
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };
  const saveApp = async () => {
    try { await api.put("/settings/app", app); setTestResult({ ok: true, msg: "App URL saved." }); }
    catch (e) { setErr(formatApiError(e)); }
  };
  const testMail = async () => {
    setBusy(true); setTestResult(null);
    try {
      const { data } = await api.post("/settings/smtp/test", { to: testTo });
      setTestResult({ ok: data.log?.status === "sent", msg: data.log?.error || `Sent to ${data.sent_to}` });
      load();
    } catch (e) { setTestResult({ ok: false, msg: formatApiError(e) }); }
    finally { setBusy(false); }
  };

  const doReset = async () => {
    setResetBusy(true); setResetResult(null);
    try {
      const { data } = await api.post("/settings/reset-data", {
        password: resetPw, confirm_text: resetConfirm, keep_users: keepUsers,
      });
      const total = Object.values(data.stats).reduce((a, b) => a + (b || 0), 0);
      setResetResult({ ok: true, msg: `Wiped ${total} records across ${Object.keys(data.stats).length} collections.` });
      setResetPw(""); setResetConfirm(""); setShowReset(false);
    } catch (e) { setResetResult({ ok: false, msg: formatApiError(e) }); }
    finally { setResetBusy(false); }
  };

  if (!smtp) return <div className="p-6 text-sm text-zinc-500">Loading settings…</div>;

  return (
    <div className="p-4 md:p-8 max-w-4xl" data-testid="settings-page">
      <PageHeader title="Settings" subtitle="SMTP email & app configuration. Notifications are sent for new requests, approvals, issues, low-stock, and recoveries." />

      {err && <div className="mb-4 text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{err}</div>}
      {testResult && (
        <div className={`mb-4 inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-md ${testResult.ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-rose-50 text-rose-700 border border-rose-200"}`}>
          {testResult.ok ? <CheckCircle2 size={14}/> : <AlertCircle size={14}/>} {testResult.msg}
        </div>
      )}

      <Card className="p-5 mb-6">
        <div className="flex items-center gap-2 mb-4"><Globe className="text-[#F97316]" size={18}/><div className="font-display font-semibold">App Public URL</div></div>
        <div className="text-xs text-zinc-500 mb-3">Used in outgoing emails so users can click through to a request. Example: <span className="num">https://inventory.sanghvi.com</span></div>
        <div className="flex gap-2">
          <input className="inp flex-1" value={app.public_url} onChange={e=>setApp(a=>({...a, public_url: e.target.value}))} data-testid="settings-app-url" placeholder="https://inventory.example.com" />
          <button onClick={saveApp} data-testid="settings-app-save" className="bg-[#18181B] hover:bg-[#27272A] text-white font-semibold text-sm py-2 px-4 rounded-md inline-flex items-center gap-2"><Save size={13}/> Save</button>
        </div>
      </Card>

      <Card className="p-5 mb-6">
        <div className="flex items-center gap-2 mb-4"><Mail className="text-[#F97316]" size={18}/><div className="font-display font-semibold">SMTP (Email)</div></div>
        <div className="text-xs text-zinc-500 mb-4">
          Gmail tip: enable 2FA on your Google account, then create an <b>App Password</b> (Google → Security → App Passwords) and paste it here. SMTP host <span className="num">smtp.gmail.com</span>, port <span className="num">587</span>, TLS on.
        </div>
        <form onSubmit={saveSmtp} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="md:col-span-2 inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!smtp.enabled} onChange={e=>setSmtp(s=>({...s, enabled: e.target.checked}))} data-testid="smtp-enabled" />
            Enable email notifications
          </label>
          <Field label="SMTP Host"><input className="inp" required value={smtp.host||""} onChange={e=>setSmtp(s=>({...s, host:e.target.value}))} data-testid="smtp-host" /></Field>
          <Field label="Port"><input className="inp" type="number" required value={smtp.port||587} onChange={e=>setSmtp(s=>({...s, port:parseInt(e.target.value)||587}))} data-testid="smtp-port" /></Field>
          <Field label="Username (Gmail address)"><input className="inp" required value={smtp.user||""} onChange={e=>setSmtp(s=>({...s, user:e.target.value}))} data-testid="smtp-user" /></Field>
          <Field label="App Password"><input className="inp" type="password" required value={smtp.password||""} onChange={e=>setSmtp(s=>({...s, password:e.target.value}))} data-testid="smtp-pass" placeholder="(hidden after save)" /></Field>
          <Field label="From Address"><input className="inp" required value={smtp.from_addr||""} onChange={e=>setSmtp(s=>({...s, from_addr:e.target.value}))} data-testid="smtp-from-addr" /></Field>
          <Field label="From Name"><input className="inp" value={smtp.from_name||"Sanghvi Infotech"} onChange={e=>setSmtp(s=>({...s, from_name:e.target.value}))} data-testid="smtp-from-name" /></Field>
          <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!smtp.use_tls} onChange={e=>setSmtp(s=>({...s, use_tls:e.target.checked, use_ssl: e.target.checked ? false : s.use_ssl}))} data-testid="smtp-tls" /> STARTTLS (Gmail uses this)</label>
          <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!smtp.use_ssl} onChange={e=>setSmtp(s=>({...s, use_ssl:e.target.checked, use_tls: e.target.checked ? false : s.use_tls}))} data-testid="smtp-ssl" /> SSL (port 465)</label>
          <button type="submit" disabled={busy} data-testid="smtp-save" className="md:col-span-2 bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold text-sm py-2.5 rounded-md inline-flex items-center justify-center gap-2"><Save size={14}/> Save SMTP Settings</button>
        </form>
        <div className="border-t border-zinc-100 mt-5 pt-5">
          <div className="text-xs uppercase tracking-widest font-bold text-zinc-700 mb-2">Send Test Email</div>
          <div className="flex gap-2">
            <input className="inp flex-1" placeholder="Recipient email" value={testTo} onChange={e=>setTestTo(e.target.value)} data-testid="smtp-test-to" />
            <button onClick={testMail} disabled={busy} data-testid="smtp-test-send" className="bg-[#18181B] hover:bg-[#27272A] text-white font-semibold text-sm py-2 px-4 rounded-md inline-flex items-center gap-2"><SendIcon size={13}/> Send Test</button>
          </div>
        </div>
      </Card>

      <Card className="p-5">
        <div className="font-display font-semibold mb-3">Recent Email Activity</div>
        {log.length === 0 ? <div className="text-xs text-zinc-500">No emails sent yet.</div> :
          <table className="w-full text-sm data-table">
            <thead className="bg-zinc-50"><tr>
              <th className="px-3 py-2 text-left">When</th>
              <th className="px-3 py-2 text-left">To</th>
              <th className="px-3 py-2 text-left">Subject</th>
              <th className="px-3 py-2 text-left">Event</th>
              <th className="px-3 py-2 text-left">Status</th>
            </tr></thead>
            <tbody>
              {log.map((e, i) => (
                <tr key={i} className="border-t border-zinc-100">
                  <td className="px-3 py-2 num text-xs">{(e.created_at||"").slice(0,19).replace("T"," ")}</td>
                  <td className="px-3 py-2 num text-xs">{Array.isArray(e.to) ? e.to.join(", ") : e.to}</td>
                  <td className="px-3 py-2">{e.subject}</td>
                  <td className="px-3 py-2 text-xs text-zinc-500">{e.event}</td>
                  <td className={`px-3 py-2 text-xs font-semibold ${e.status === "sent" ? "text-emerald-600" : e.status?.startsWith("err") ? "text-rose-600" : "text-zinc-500"}`}>{e.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        }
      </Card>

      {/* DANGER ZONE */}
      <Card className="p-5 mt-6 border-rose-200 bg-rose-50/40" data-testid="danger-zone">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="text-rose-600" size={18}/>
          <div className="font-display font-semibold text-rose-700">Danger Zone — Reset All Data</div>
        </div>
        <div className="text-xs text-zinc-600 mb-4 max-w-2xl">
          Wipes every product, warehouse, request, issue, recovery, dispatch, project, POP, audit log, email log, and uploaded file record. Your own admin account is always kept. Use this once, on a fresh production deploy, to clear any leftover data.
        </div>
        {resetResult && (
          <div className={`mb-3 inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-md ${resetResult.ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-rose-50 text-rose-700 border border-rose-200"}`}>
            {resetResult.ok ? <CheckCircle2 size={14}/> : <AlertCircle size={14}/>} {resetResult.msg}
          </div>
        )}
        {!showReset ? (
          <button onClick={()=>{setShowReset(true); setResetResult(null);}} data-testid="reset-open-btn" className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white font-semibold text-sm py-2 px-4 rounded-md">
            <Trash2 size={13}/> Reset All Data…
          </button>
        ) : (
          <div className="space-y-3 border border-rose-200 rounded-md p-4 bg-white max-w-2xl" data-testid="reset-form">
            <div className="text-xs text-rose-700 font-semibold">This cannot be undone.</div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-zinc-700 mb-1">Your admin password</label>
              <input type="password" className="inp" value={resetPw} onChange={e=>setResetPw(e.target.value)} data-testid="reset-password" autoComplete="off" />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-zinc-700 mb-1">Type <span className="num font-bold">RESET ALL DATA</span> to confirm</label>
              <input className="inp" value={resetConfirm} onChange={e=>setResetConfirm(e.target.value)} data-testid="reset-confirm" placeholder="RESET ALL DATA" autoComplete="off" />
            </div>
            <label className="inline-flex items-center gap-2 text-xs">
              <input type="checkbox" checked={keepUsers} onChange={e=>setKeepUsers(e.target.checked)} data-testid="reset-keep-users" />
              Keep all user accounts (recommended). Uncheck to also delete all non-admin users.
            </label>
            <div className="flex gap-2">
              <button onClick={doReset} disabled={resetBusy || !resetPw || resetConfirm !== "RESET ALL DATA"} data-testid="reset-confirm-btn"
                className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 text-white font-semibold text-sm py-2 px-4 rounded-md">
                <Trash2 size={13}/> {resetBusy ? "Wiping…" : "Yes, wipe everything"}
              </button>
              <button onClick={()=>{setShowReset(false); setResetPw(""); setResetConfirm("");}} className="text-sm font-semibold text-zinc-600 hover:text-zinc-900 py-2 px-4 rounded-md">
                Cancel
              </button>
            </div>
          </div>
        )}
      </Card>
      <style>{`.inp { width:100%; border:1px solid #E4E4E7; background:#fff; border-radius:.375rem; padding:.5rem .75rem; font-size:.85rem; outline:none } .inp:focus { box-shadow: 0 0 0 2px #F97316; border-color: transparent }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-bold uppercase tracking-widest text-zinc-700 mb-1">{label}</label>
      {children}
    </div>
  );
}
